import { CreateInstitutionRequest, InstitutionDTO, UpdateInstitutionRequest } from '@/models';
import { useInstitutionStore, useUIStore } from '@/store';
import { getErrorMessage } from '@/utils/getErrorMessage';
import React, { useCallback, useEffect, useState } from 'react';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 10;

const createInitialFormData = (): CreateInstitutionRequest => ({
  name: '',
  address: '',
  city: '',
  province: '',
  postalCode: '',
  phoneNumber: '',
  email: '',
  contact: '',
  accountNumber: '',
  safetyVideoUrl: '',
  emergencyContactPhone: '',
  emergencyContactName: '',
  emergencyContactEmail: '',
  emergencyContactMessage: '',
  certificationConfiguration: '',
  logoIconUrl: '',
  logoFullUrl: '',
});

/**
 * Manages institution CRUD operations, pagination, and modal state
 * for the Institution Management page
 */
export const useInstitutionManagement = () => {
  const {
    institutions,
    pagination,
    isLoading,
    error,
    fetchInstitutions,
    createInstitution,
    updateInstitution,
    deleteInstitution,
    clearError,
  } = useInstitutionStore();

  const { addNotification } = useUIStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedInstitution, setSelectedInstitution] = useState<InstitutionDTO | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [institutionToDelete, setInstitutionToDelete] = useState<InstitutionDTO | null>(null);
  const [formData, setFormData] = useState<CreateInstitutionRequest>(createInitialFormData);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchInstitutions(currentPage, pageSize);
  }, [currentPage, pageSize, fetchInstitutions]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  }, []);

  const handleCreateClick = useCallback(() => {
    setModalMode('create');
    setSelectedInstitution(null);
    setFormData(createInitialFormData());
    setShowModal(true);
    clearError();
  }, [clearError]);

  const handleEditClick = useCallback(
    (institution: InstitutionDTO) => {
      setModalMode('edit');
      setSelectedInstitution(institution);
      setFormData({
        ...createInitialFormData(),
        name: institution.name || '',
        address: institution.address || '',
        city: institution.city || '',
        province: institution.province || '',
        postalCode: institution.postalCode || '',
        phoneNumber: institution.phoneNumber || '',
        email: institution.email || '',
        contact: institution.contact || '',
        accountNumber: institution.accountNumber || '',
        safetyVideoUrl: institution.safetyVideoUrl || '',
        emergencyContactPhone: institution.emergencyContactPhone || '',
        emergencyContactName: institution.emergencyContactName || '',
        emergencyContactEmail: institution.emergencyContactEmail || '',
        emergencyContactMessage: institution.emergencyContactMessage || '',
        certificationConfiguration: institution.certificationConfiguration || '',
        logoIconUrl: institution.logoIconUrl || '',
        logoFullUrl: institution.logoFullUrl || '',
      });
      setShowModal(true);
      clearError();
    },
    [clearError]
  );

  const handleSubmit = useCallback(async () => {
    setIsSaving(true);
    try {
      if (modalMode === 'create') {
        await createInstitution(formData);
        addNotification({
          type: 'success',
          message: 'Institution created successfully',
          duration: 2000,
        });
      } else if (selectedInstitution) {
        const updateData: UpdateInstitutionRequest = {
          name: formData.name,
          address: formData.address,
          city: formData.city,
          province: formData.province,
          postalCode: formData.postalCode,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          contact: formData.contact,
          accountNumber: formData.accountNumber,
          safetyVideoUrl: formData.safetyVideoUrl,
          emergencyContactPhone: formData.emergencyContactPhone,
          emergencyContactName: formData.emergencyContactName,
          emergencyContactEmail: formData.emergencyContactEmail,
          emergencyContactMessage: formData.emergencyContactMessage,
          certificationConfiguration: formData.certificationConfiguration,
          logoIconUrl: formData.logoIconUrl,
          logoFullUrl: formData.logoFullUrl,
        };
        await updateInstitution(selectedInstitution.id, updateData);
        addNotification({
          type: 'success',
          message: 'Institution updated successfully',
          duration: 2000,
        });
      }
      setShowModal(false);
      await fetchInstitutions(currentPage, pageSize);
    } catch (err: unknown) {
      addNotification({
        type: 'error',
        message: getErrorMessage(err, 'Operation failed'),
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    modalMode,
    createInstitution,
    formData,
    selectedInstitution,
    updateInstitution,
    fetchInstitutions,
    currentPage,
    pageSize,
    addNotification,
  ]);

  const handleFieldChange = useCallback((field: keyof CreateInstitutionRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      handleFieldChange(e.target.name as keyof CreateInstitutionRequest, e.target.value);
    },
    [handleFieldChange]
  );

  const handleDeleteClick = useCallback(
    (institution: InstitutionDTO) => {
      setInstitutionToDelete(institution);
      setShowDeleteModal(true);
      clearError();
    },
    [clearError]
  );

  const handleDelete = useCallback(async () => {
    if (!institutionToDelete) return;
    setIsSaving(true);
    try {
      await deleteInstitution(institutionToDelete.id);
      addNotification({
        type: 'success',
        message: 'Institution deleted successfully',
        duration: 2000,
      });
      setShowDeleteModal(false);
      await fetchInstitutions(currentPage, pageSize);
    } catch (err: unknown) {
      addNotification({
        type: 'error',
        message: getErrorMessage(err, 'Operation failed'),
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    institutionToDelete,
    deleteInstitution,
    fetchInstitutions,
    currentPage,
    pageSize,
    addNotification,
  ]);

  const closeModal = useCallback(() => setShowModal(false), []);
  const closeDeleteModal = useCallback(() => setShowDeleteModal(false), []);

  return {
    institutions,
    pagination,
    isLoading,
    error,
    currentPage,
    pageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showModal,
    modalMode,
    selectedInstitution,
    showDeleteModal,
    institutionToDelete,
    formData,
    isSaving,
    setCurrentPage,
    handlePageSizeChange,
    handleCreateClick,
    handleEditClick,
    handleDeleteClick,
    handleSubmit,
    handleDelete,
    handleFieldChange,
    handleInputChange,
    closeModal,
    closeDeleteModal,
    clearError,
  };
};
